<template>
  <ClientOnly>
    <div
      v-if="open && isActive"
      class="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div
        ref="dialogEl"
        class="relative w-full max-w-md rounded bg-white shadow-2xl"
      >
        <button
          class="absolute right-4 top-4 z-10 text-2xl text-gray-500"
          @click="toggle()"
        >
          <Icon name="mdi:close" />
        </button>
        <NuxtLink :to="bannerLinkWithUTM(bannerLink)" target="_blank">
          <NuxtImg
            v-if="bannerImage"
            provider="sanity"
            :src="bannerImage.asset?._ref"
            alt="Popover Banner"
            class="w-full"
          />
        </NuxtLink>
        <p v-if="bannerText" class="p-4" :class="[bannerTextAlign]">
          {{ bannerText }}
        </p>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { getAll } from "~/groq/banners"
import type { Banners } from "~/@types/schema"

type TextAlignment = Required<Banners>["popover"]["textAlignment"]

const textAlignmentClasses: Record<TextAlignment, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
}

const { data: banners } = await useSanityQuery<Banners>(getAll())
const popoverBanner = banners.value?.popover
const isActive = computed(() => popoverBanner?.isActive)
const bannerImage = computed(() => popoverBanner?.image)
const bannerTextAlign = computed(
  () =>
    textAlignmentClasses[popoverBanner?.textAlignment as TextAlignment] ||
    "text-left",
)
const bannerText = computed(() => popoverBanner?.bannerText || "")
const bannerLink = computed(() => popoverBanner?.link || "#")
const bannerIdentifier = computed(() => popoverBanner?.uniqueId || "default")

const dialogEl = ref<HTMLElement | null>(null)
const cookie = useCookie("popover-banner-shown", {
  default: () => "",
  maxAge: 60 * 60, // 1 hour
})

const [open, toggle] = useToggle(bannerIdentifier.value !== cookie.value)

const bannerLinkWithUTM = (link: string) => {
  return linkWithUTM(link, "calibre_careers", "banner", "popover_banner")
}

onClickOutside(dialogEl, () => {
  if (open.value) {
    toggle()
  }
})

onMounted(() => {
  cookie.value = bannerIdentifier.value
})
</script>
